import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AskFrankDesk, {
    DeskRibbon,
    DeskTitle,
    DeskNote,
    DeskPanel,
    DeskField,
    DeskEmpty,
    Pill,
    formatNumber,
    formatDateTime,
    DESK_INPUT_CLASS,
    DESK_BUTTON_CLASS,
    DESK_PRIMARY_CLASS,
    DESK_PRIMARY_STYLE,
    DESK_DANGER_CLASS,
    TINY_BUTTON_CLASS,
} from '../../../../components/staff/askfrank/AskFrankDesk';
import {
    getAskFrankSources,
    createAskFrankSource,
    updateAskFrankSource,
    deleteAskFrankSource,
    checkAskFrankRobots,
    requeueAskFrankSource,
    diagnoseAskFrankSource,
    getAskFrankSourcePages,
    recrawlAskFrankPage,
    deleteAskFrankPage,
    getAskFrankRules,
    saveAskFrankRule,
    deleteAskFrankRule,
} from '../../../../services/api';

const SOURCE_TYPES = ['fansite', 'social', 'official', 'wiki', 'forum', 'other'];

const STATUS_TONES = {
    pending: 'yellow',
    approved: 'green',
    paused: 'orange',
    rejected: 'red',
};

const EMPTY_FORM = {
    domain: '',
    name: '',
    seedUrl: '',
    sourceType: 'fansite',
    status: 'approved',
    description: '',
    trustWeight: 1,
    maxPages: 300,
    maxDepth: 3,
    crawlDelaySeconds: 30,
};

const DETAIL_TABS = [
    { key: 'settings', label: 'Settings' },
    { key: 'diagnose', label: 'Why idle' },
    { key: 'pages', label: 'Pages' },
    { key: 'rules', label: 'URL rules' },
];

function SourceRow({ source, open, busy, onToggleStatus, onRequeue, onOpen }) {
    return (
        <div className={`flex flex-wrap items-center gap-2 border-b border-gray-200 px-3 py-2 last:border-b-0 ${open ? 'bg-white' : ''}`}>
            <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                    <span className="font-['Jersey_10'] text-[17px] uppercase tracking-[1px] text-gray-900">
                        {source.name}
                    </span>
                    <Pill tone={STATUS_TONES[source.status]}>{source.status}</Pill>
                    <span className="text-[10px] uppercase text-gray-400">{source.sourceType}</span>
                    {!source.robotsAllowed && <Pill tone="red">robots blocked</Pill>}
                    {source.allowsIframe === false && <Pill>no iframe</Pill>}
                </div>

                <p className="mt-[2px] text-[11px] text-gray-400">
                    {source.domain}
                    {' | '}{formatNumber(source.indexedCount ?? 0)} indexed of {formatNumber(source.pageCount)} known
                    {' | '}last crawl {formatDateTime(source.lastCrawledAt)}
                </p>

                {source.submittedNote && (
                    <p className="mt-1 text-[11px] italic text-gray-600">
                        &ldquo;{source.submittedNote}&rdquo;
                        {source.submittedByName ? ` - ${source.submittedByName}` : ''}
                    </p>
                )}

                {source.lastError && <p className="mt-1 text-[11px] text-red-700">{source.lastError}</p>}
            </div>

            <div className="flex shrink-0 flex-wrap gap-1">
                <button
                    type="button"
                    disabled={busy}
                    onClick={() => onToggleStatus(source)}
                    className={`px-2 py-1 text-[11px] uppercase text-white disabled:opacity-50 ${
                        source.status === 'approved' ? 'bg-orange-500' : 'bg-green-600'
                    }`}
                >
                    {source.status === 'approved' ? 'Pause' : 'Approve'}
                </button>

                <button
                    type="button"
                    disabled={busy}
                    onClick={() => onRequeue(source.id)}
                    className={TINY_BUTTON_CLASS}
                    title="Put this site's pages back in the crawl queue"
                >
                    Requeue
                </button>

                <button type="button" onClick={() => onOpen(source)} className={TINY_BUTTON_CLASS}>
                    {open ? 'Close' : 'Open'}
                </button>
            </div>
        </div>
    );
}

function AskFrankSources({ userData }) {
    const [sources, setSources] = useState([]);
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [statusFilter, setStatusFilter] = useState('');
    const [adding, setAdding] = useState(false);
    const [showLimits, setShowLimits] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [openId, setOpenId] = useState(null);
    const [detailTab, setDetailTab] = useState('settings');
    const [settings, setSettings] = useState(null);
    const [diagnosis, setDiagnosis] = useState(null);
    const [pages, setPages] = useState(null);
    const [ruleForm, setRuleForm] = useState({ ruleType: 'deny', pattern: '', note: '' });

    const load = useCallback(async () => {
        const [sourceRes, ruleRes] = await Promise.all([getAskFrankSources(), getAskFrankRules()]);

        if (sourceRes.success) setSources(sourceRes.sources || []);
        else toast.error(sourceRes.message || 'Could not load AskFrank sources');

        if (ruleRes.success) setRules(ruleRes.rules || []);

        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const openSource = useMemo(() => sources.find(s => s.id === openId) || null, [sources, openId]);

    useEffect(() => {
        if (!openSource) {
            setSettings(null);
            return;
        }

        setSettings({
            name: openSource.name,
            description: openSource.description || '',
            sourceType: openSource.sourceType,
            seedUrl: openSource.seedUrl || '',
            trustWeight: openSource.trustWeight,
            maxPages: openSource.maxPages,
            maxDepth: openSource.maxDepth,
            crawlDelaySeconds: openSource.crawlDelaySeconds,
        });
    }, [openSource]);

    if (userData && !userData.hasAdminRole) return <Navigate to="/staff" replace />;

    const withBusy = async (fn, successMessage) => {
        setBusy(true);
        try {
            const res = await fn();
            if (res.success) {
                if (successMessage) toast.success(res.message || successMessage);
                await load();
            } else {
                toast.error(res.message || 'That did not work');
            }
            return res;
        } finally {
            setBusy(false);
        }
    };

    const handleOpen = (source) => {
        if (openId === source.id) {
            setOpenId(null);
            return;
        }

        setOpenId(source.id);
        setDetailTab('settings');
        setDiagnosis(null);
        setPages(null);
    };

    const handleCreate = async (event) => {
        event.preventDefault();

        if (!form.domain.trim()) {
            toast.error('A domain is required');
            return;
        }

        const res = await withBusy(() => createAskFrankSource(form), 'Source added');
        if (res.success) {
            setForm(EMPTY_FORM);
            setAdding(false);
            setShowLimits(false);
        }
    };

    const handleRobots = async () => {
        const res = await withBusy(() => checkAskFrankRobots(openId));
        if (res.success) {
            toast.info(
                res.robotsAllowed
                    ? `robots.txt allows crawling${res.crawlDelay ? ` (crawl-delay ${res.crawlDelay}s)` : ''}`
                    : 'robots.txt blocks crawling this site'
            );
        }
    };

    const runDiagnosis = async () => {
        const res = await diagnoseAskFrankSource(openId);
        if (res.success) setDiagnosis(res);
        else toast.error(res.message || 'Could not read that source.');
    };

    const loadPages = async () => {
        const res = await getAskFrankSourcePages(openId, 1);
        if (res.success) setPages(res.pages || []);
        else toast.error('Could not load pages');
    };

    const selectTab = (key) => {
        setDetailTab(key);
        if (key === 'diagnose' && !diagnosis) runDiagnosis();
        if (key === 'pages' && !pages) loadPages();
    };

    const submitRule = async (event) => {
        event.preventDefault();

        if (!ruleForm.pattern.trim()) {
            toast.error('A pattern is required');
            return;
        }

        const res = await withBusy(
            () => saveAskFrankRule({ ...ruleForm, sourceId: openId }),
            'Rule added'
        );
        if (res.success) setRuleForm({ ruleType: 'deny', pattern: '', note: '' });
    };

    const visibleSources = statusFilter
        ? sources.filter(source => source.status === statusFilter)
        : sources;

    const sourceRules = rules.filter(rule => rule.sourceId === openId);

    return (
        <AskFrankDesk userData={userData}>
            <DeskRibbon>
                <DeskTitle>Crawl Sources</DeskTitle>
                <DeskNote>The sites Frank is allowed to read, and how hard he is allowed to read them.</DeskNote>

                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="ml-auto border border-gray-300 px-2 py-[5px] text-[13px]"
                >
                    <option value="">All statuses</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="paused">Paused</option>
                    <option value="rejected">Rejected</option>
                </select>

                <button
                    type="button"
                    onClick={() => setAdding(v => !v)}
                    className={DESK_PRIMARY_CLASS}
                    style={DESK_PRIMARY_STYLE}
                >
                    {adding ? 'Cancel' : 'Add a source'}
                </button>
            </DeskRibbon>

            {adding && (
                <DeskPanel title="Add a source">
                    <form onSubmit={handleCreate} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <DeskField label="Domain" required hint="no https, no slash">
                            <input
                                type="text"
                                value={form.domain}
                                onChange={(e) => setForm({ ...form, domain: e.target.value })}
                                placeholder="habbowidgets.com"
                                className={DESK_INPUT_CLASS}
                            />
                        </DeskField>

                        <DeskField label="Display name" hint="defaults to the domain">
                            <input
                                type="text"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="HabboWidgets"
                                className={DESK_INPUT_CLASS}
                            />
                        </DeskField>

                        <DeskField label="Start page" hint="where the crawl begins">
                            <input
                                type="text"
                                value={form.seedUrl}
                                onChange={(e) => setForm({ ...form, seedUrl: e.target.value })}
                                placeholder="https://habbowidgets.com/"
                                className={DESK_INPUT_CLASS}
                            />
                        </DeskField>

                        <DeskField label="Type">
                            <select
                                value={form.sourceType}
                                onChange={(e) => setForm({ ...form, sourceType: e.target.value })}
                                className={DESK_INPUT_CLASS}
                            >
                                {SOURCE_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
                            </select>
                        </DeskField>

                        <div className="sm:col-span-2">
                            <button
                                type="button"
                                onClick={() => setShowLimits(v => !v)}
                                className={DESK_BUTTON_CLASS}
                            >
                                {showLimits ? 'Hide crawl limits' : 'Crawl limits'}
                            </button>
                            <span className="ml-2 text-[11px] text-gray-400">
                                {form.maxPages} pages, depth {form.maxDepth}, {form.crawlDelaySeconds}s apart, trust {form.trustWeight}
                            </span>
                        </div>

                        {showLimits && (
                            <>
                                <DeskField label="Max pages">
                                    <input
                                        type="number"
                                        min="1"
                                        max="20000"
                                        value={form.maxPages}
                                        onChange={(e) => setForm({ ...form, maxPages: Number(e.target.value) })}
                                        className={DESK_INPUT_CLASS}
                                    />
                                </DeskField>

                                <DeskField label="Max depth" hint="clicks from the start page">
                                    <input
                                        type="number"
                                        min="0"
                                        max="6"
                                        value={form.maxDepth}
                                        onChange={(e) => setForm({ ...form, maxDepth: Number(e.target.value) })}
                                        className={DESK_INPUT_CLASS}
                                    />
                                </DeskField>

                                <DeskField label="Crawl delay" hint="seconds between requests">
                                    <input
                                        type="number"
                                        min="5"
                                        max="600"
                                        value={form.crawlDelaySeconds}
                                        onChange={(e) => setForm({ ...form, crawlDelaySeconds: Number(e.target.value) })}
                                        className={DESK_INPUT_CLASS}
                                    />
                                </DeskField>

                                <DeskField label="Trust weight" hint="how much its answers count">
                                    <input
                                        type="number"
                                        step="0.1"
                                        min="0.1"
                                        max="3"
                                        value={form.trustWeight}
                                        onChange={(e) => setForm({ ...form, trustWeight: Number(e.target.value) })}
                                        className={DESK_INPUT_CLASS}
                                    />
                                </DeskField>

                                <DeskField label="Status" className="sm:col-span-2">
                                    <select
                                        value={form.status}
                                        onChange={(e) => setForm({ ...form, status: e.target.value })}
                                        className={DESK_INPUT_CLASS}
                                    >
                                        <option value="approved">approved</option>
                                        <option value="pending">pending</option>
                                        <option value="paused">paused</option>
                                    </select>
                                </DeskField>
                            </>
                        )}

                        <div className="sm:col-span-2">
                            <button type="submit" disabled={busy} className={DESK_PRIMARY_CLASS} style={DESK_PRIMARY_STYLE}>
                                Add source
                            </button>
                        </div>
                    </form>
                </DeskPanel>
            )}

            <DeskPanel
                title="Sources"
                padding="p-0"
                right={<span className="text-[11px] uppercase text-white/70">{visibleSources.length} listed</span>}
            >
                {loading ? (
                    <DeskEmpty>Loading...</DeskEmpty>
                ) : visibleSources.length === 0 ? (
                    <DeskEmpty>No sources here. Add one and Frank will start reading it.</DeskEmpty>
                ) : (
                    visibleSources.map(source => (
                        <SourceRow
                            key={source.id}
                            source={source}
                            open={openId === source.id}
                            busy={busy}
                            onToggleStatus={(row) => withBusy(
                                () => updateAskFrankSource(row.id, { status: row.status === 'approved' ? 'paused' : 'approved' }),
                                'Updated'
                            )}
                            onRequeue={(id) => withBusy(() => requeueAskFrankSource(id), 'Requeued')}
                            onOpen={handleOpen}
                        />
                    ))
                )}
            </DeskPanel>

            {openSource && settings && (
                <DeskPanel
                    title={openSource.name}
                    right={
                        <button type="button" onClick={() => setOpenId(null)} className="text-[11px] uppercase text-white/80 hover:text-white">
                            Close
                        </button>
                    }
                >
                    <div className="mb-3 flex flex-wrap gap-1 border-b border-gray-300">
                        {DETAIL_TABS.map(entry => (
                            <button
                                key={entry.key}
                                type="button"
                                onClick={() => selectTab(entry.key)}
                                className={`px-3 py-[6px] font-['Jersey_10'] text-[15px] uppercase tracking-[1px] ${
                                    detailTab === entry.key ? 'bg-maroon text-white' : 'bg-white text-maroon hover:bg-gray-100'
                                }`}
                            >
                                {entry.label}
                            </button>
                        ))}
                    </div>

                    {detailTab === 'settings' && (
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <DeskField label="Display name">
                                <input
                                    type="text"
                                    value={settings.name}
                                    onChange={(e) => setSettings({ ...settings, name: e.target.value })}
                                    className={DESK_INPUT_CLASS}
                                />
                            </DeskField>

                            <DeskField label="Type">
                                <select
                                    value={settings.sourceType}
                                    onChange={(e) => setSettings({ ...settings, sourceType: e.target.value })}
                                    className={DESK_INPUT_CLASS}
                                >
                                    {SOURCE_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
                                </select>
                            </DeskField>

                            <DeskField label="Start page" className="sm:col-span-2">
                                <input
                                    type="text"
                                    value={settings.seedUrl}
                                    onChange={(e) => setSettings({ ...settings, seedUrl: e.target.value })}
                                    placeholder={`https://${openSource.domain}/`}
                                    className={DESK_INPUT_CLASS}
                                />
                            </DeskField>

                            <DeskField label="Description" className="sm:col-span-2">
                                <input
                                    type="text"
                                    value={settings.description}
                                    onChange={(e) => setSettings({ ...settings, description: e.target.value })}
                                    className={DESK_INPUT_CLASS}
                                />
                            </DeskField>

                            <DeskField label="Max pages">
                                <input
                                    type="number"
                                    min="1"
                                    max="20000"
                                    value={settings.maxPages}
                                    onChange={(e) => setSettings({ ...settings, maxPages: Number(e.target.value) })}
                                    className={DESK_INPUT_CLASS}
                                />
                            </DeskField>

                            <DeskField label="Max depth">
                                <input
                                    type="number"
                                    min="0"
                                    max="6"
                                    value={settings.maxDepth}
                                    onChange={(e) => setSettings({ ...settings, maxDepth: Number(e.target.value) })}
                                    className={DESK_INPUT_CLASS}
                                />
                            </DeskField>

                            <DeskField label="Crawl delay" hint="seconds">
                                <input
                                    type="number"
                                    min="5"
                                    max="600"
                                    value={settings.crawlDelaySeconds}
                                    onChange={(e) => setSettings({ ...settings, crawlDelaySeconds: Number(e.target.value) })}
                                    className={DESK_INPUT_CLASS}
                                />
                            </DeskField>

                            <DeskField label="Trust weight">
                                <input
                                    type="number"
                                    step="0.1"
                                    min="0.1"
                                    max="3"
                                    value={settings.trustWeight}
                                    onChange={(e) => setSettings({ ...settings, trustWeight: Number(e.target.value) })}
                                    className={DESK_INPUT_CLASS}
                                />
                            </DeskField>

                            <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
                                <button
                                    type="button"
                                    disabled={busy}
                                    onClick={() => withBusy(() => updateAskFrankSource(openId, settings), 'Settings saved')}
                                    className={DESK_PRIMARY_CLASS}
                                    style={DESK_PRIMARY_STYLE}
                                >
                                    Save settings
                                </button>

                                <button type="button" disabled={busy} onClick={handleRobots} className={DESK_BUTTON_CLASS}>
                                    Check robots.txt
                                </button>

                                <button
                                    type="button"
                                    disabled={busy}
                                    onClick={() => withBusy(() => updateAskFrankSource(openId, { status: 'rejected' }), 'Rejected')}
                                    className={`${DESK_BUTTON_CLASS} ml-auto`}
                                >
                                    Reject
                                </button>

                                <button
                                    type="button"
                                    disabled={busy}
                                    onClick={() => {
                                        if (!window.confirm(`Delete ${openSource.name} and everything Frank has indexed from it?`)) return;
                                        setOpenId(null);
                                        withBusy(() => deleteAskFrankSource(openSource.id), 'Source removed');
                                    }}
                                    className={DESK_DANGER_CLASS}
                                >
                                    Delete source
                                </button>
                            </div>
                        </div>
                    )}

                    {detailTab === 'diagnose' && (
                        <div>
                            <button type="button" onClick={runDiagnosis} className={`${DESK_BUTTON_CLASS} mb-3`}>
                                Check again
                            </button>

                            {!diagnosis ? (
                                <DeskEmpty>Reading the crawl queue...</DeskEmpty>
                            ) : (
                                <>
                                    <p className="mb-2 font-['Jersey_10'] text-[18px] uppercase tracking-[1px] text-maroon">
                                        {diagnosis.claimableNow > 0 ? 'Ready to crawl' : 'Not crawling'}
                                    </p>

                                    <p className="mb-3 text-[14px] text-gray-800">{diagnosis.verdict}</p>

                                    <div className="mb-3 grid grid-cols-2 gap-2 text-[12px] sm:grid-cols-4">
                                        <div>Status: <strong>{diagnosis.source.status}</strong></div>
                                        <div>
                                            Robots: <strong>
                                                {diagnosis.source.robotsCheckedAt
                                                    ? (diagnosis.source.robotsAllowed ? 'allowed' : 'blocked')
                                                    : 'never checked'}
                                            </strong>
                                        </div>
                                        <div>Due now: <strong>{diagnosis.claimableNow}</strong></div>
                                        <div>Delay: <strong>{diagnosis.source.crawlDelaySeconds}s</strong></div>
                                        <div className="col-span-2 truncate sm:col-span-4">
                                            Start page: <strong>{diagnosis.source.seedUrl || 'none set'}</strong>
                                        </div>
                                    </div>

                                    {diagnosis.pages.length > 0 && (
                                        <div className="mb-3 flex flex-wrap gap-2 text-[12px]">
                                            {diagnosis.pages.map(row => (
                                                <span key={row.status} className="border border-gray-300 bg-white px-2 py-1">
                                                    {row.status}: <strong>{row.total}</strong>
                                                    {row.soonestCrawl && (
                                                        <span className="ml-1 text-gray-500">next {formatDateTime(row.soonestCrawl)}</span>
                                                    )}
                                                </span>
                                            ))}
                                        </div>
                                    )}

                                    {diagnosis.recentErrors.length > 0 && (
                                        <div className="text-[12px]">
                                            <p className="mb-1 uppercase tracking-[1px] text-gray-500">Recent errors</p>
                                            {diagnosis.recentErrors.map(err => (
                                                <div key={err.url} className="border-b border-gray-200 py-1">
                                                    <span className="text-red-700">{err.httpStatus || 'no status'}</span>
                                                    <span className="ml-2 text-gray-700">{err.lastError}</span>
                                                    <div className="truncate text-gray-400">{err.url}</div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {detailTab === 'pages' && (
                        <div>
                            <button type="button" onClick={loadPages} className={`${DESK_BUTTON_CLASS} mb-3`}>
                                Reload
                            </button>

                            {!pages ? (
                                <DeskEmpty>Loading pages...</DeskEmpty>
                            ) : pages.length === 0 ? (
                                <DeskEmpty>Frank has not found a single page here yet.</DeskEmpty>
                            ) : (
                                <div className="max-h-[420px] overflow-y-auto border border-gray-200 bg-white">
                                    {pages.map(page => (
                                        <div key={page.id} className="flex items-start gap-2 border-b border-gray-200 px-2 py-[6px] last:border-b-0">
                                            <div className="min-w-0 flex-1">
                                                <p className="truncate text-[12px] text-gray-800">{page.title || page.url}</p>
                                                <p className="truncate text-[11px] text-gray-400">{page.url}</p>
                                                <p className="mt-[2px] text-[10px] uppercase text-gray-400">
                                                    {page.status}
                                                    {page.httpStatus ? ` | HTTP ${page.httpStatus}` : ''}
                                                    {` | ${page.wordCount} words | depth ${page.depth}`}
                                                </p>
                                                {page.lastError && <p className="text-[10px] text-red-700">{page.lastError}</p>}
                                            </div>

                                            <div className="flex shrink-0 gap-1">
                                                <button
                                                    type="button"
                                                    disabled={busy}
                                                    onClick={() => withBusy(() => recrawlAskFrankPage(page.id), 'Recrawled').then(loadPages)}
                                                    className={TINY_BUTTON_CLASS}
                                                >
                                                    Recrawl
                                                </button>
                                                <button
                                                    type="button"
                                                    disabled={busy}
                                                    onClick={() => withBusy(() => deleteAskFrankPage(page.id), 'Removed').then(loadPages)}
                                                    className="bg-red-700 px-2 py-[2px] text-[10px] uppercase text-white disabled:opacity-50"
                                                >
                                                    Drop
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {detailTab === 'rules' && (
                        <div>
                            <p className="mb-3 text-[12px] text-gray-500">
                                Patterns matched against the URL. A deny rule keeps Frank off paths that waste his crawl
                                budget, like tag listings and forum paginations. An allow rule pins him to a section.
                            </p>

                            <form onSubmit={submitRule} className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-4">
                                <DeskField label="Rule">
                                    <select
                                        value={ruleForm.ruleType}
                                        onChange={(e) => setRuleForm({ ...ruleForm, ruleType: e.target.value })}
                                        className={DESK_INPUT_CLASS}
                                    >
                                        <option value="deny">deny</option>
                                        <option value="allow">allow</option>
                                    </select>
                                </DeskField>

                                <DeskField label="Pattern" className="sm:col-span-2">
                                    <input
                                        type="text"
                                        value={ruleForm.pattern}
                                        onChange={(e) => setRuleForm({ ...ruleForm, pattern: e.target.value })}
                                        placeholder="/tag/"
                                        className={DESK_INPUT_CLASS}
                                    />
                                </DeskField>

                                <div className="flex items-end">
                                    <button type="submit" disabled={busy} className={DESK_PRIMARY_CLASS} style={DESK_PRIMARY_STYLE}>
                                        Add rule
                                    </button>
                                </div>

                                <DeskField label="Note" className="sm:col-span-4">
                                    <input
                                        type="text"
                                        value={ruleForm.note}
                                        onChange={(e) => setRuleForm({ ...ruleForm, note: e.target.value })}
                                        placeholder="Why this rule exists"
                                        className={DESK_INPUT_CLASS}
                                    />
                                </DeskField>
                            </form>

                            {sourceRules.length === 0 ? (
                                <DeskEmpty>No rules for this source.</DeskEmpty>
                            ) : (
                                <div className="border border-gray-200 bg-white">
                                    {sourceRules.map(rule => (
                                        <div key={rule.id} className="flex flex-wrap items-center gap-2 border-b border-gray-200 px-2 py-[6px] last:border-b-0">
                                            <Pill tone={rule.ruleType === 'allow' ? 'green' : 'red'}>{rule.ruleType}</Pill>
                                            <span className="min-w-0 flex-1 truncate text-[12px] text-gray-800">{rule.pattern}</span>
                                            {rule.note && <span className="truncate text-[11px] text-gray-400">{rule.note}</span>}
                                            <button
                                                type="button"
                                                disabled={busy}
                                                onClick={() => withBusy(() => deleteAskFrankRule(rule.id), 'Rule removed')}
                                                className="bg-red-700 px-2 py-[2px] text-[10px] uppercase text-white disabled:opacity-50"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </DeskPanel>
            )}
        </AskFrankDesk>
    );
}

export default AskFrankSources;
